import cx from "classnames";
import TooltipWrapper from "../../TooltipWrapper";
import { FormFieldStates, InputProps } from "./exports";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { FC, InputHTMLAttributes, PropsWithChildren, RefObject, useMemo, useState } from "react";

export interface QESDateTimeInputProps extends InputProps, InputHTMLAttributes<HTMLInputElement> {
  /** Earliest selectable datetime in YYYY-MM-DDTHH:mm format */
  min?: string;
  /** Latest selectable datetime in YYYY-MM-DDTHH:mm format */
  max?: string;
}

export const QESDateTimeInput: FC<PropsWithChildren<QESDateTimeInputProps>> = ({ containerClassName, descriptiveText, errorText, hideLabel, inlineLabel, isValid, label, labelWidth, qesRef, required, testId, wasFocusedExternally, unrequiredValidation, validationTooltipContent, ...props }) => {
  const [focused, setFocused] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(wasFocusedExternally);

  const fieldState = useMemo(() => {
    if (!isValid && (required || unrequiredValidation) && (hasBeenFocused || focused || wasFocusedExternally)) {
      return FormFieldStates.Error;
    }
    return FormFieldStates.None;
  }, [isValid, required, unrequiredValidation, hasBeenFocused, focused, wasFocusedExternally]);

  const subText = useMemo(() => {
    if (hasBeenFocused && errorText && fieldState === FormFieldStates.Error) {
      return errorText;
    } else if (descriptiveText) {
      return descriptiveText;
    }
    return "";
  }, [descriptiveText, errorText, fieldState, hasBeenFocused]);

  const handleOnBlur = () => {
    if (!hasBeenFocused) {
      setHasBeenFocused(true);
    }
    setFocused(false);
  };

  return (
    <div
      className={cx({
        [containerClassName]: !!containerClassName,
        "inline-label": inlineLabel,
        "qes-text-input__container": true,
      })}
      key={testId}
      onBlur={() => handleOnBlur()}
    >
      <label
        htmlFor={props.id || testId}
        className={cx({
          "qes-text-input__label": true,
          "visually-hidden-label": hideLabel,
        })}
        style={{ width: labelWidth ?? "auto" }}
      >
        {label}
        {required || validationTooltipContent ? (
          <span className="qes-text-input__label-subcontent">
            {required ? <span className="text-red500"> *</span> : ""}
            {validationTooltipContent ? <TooltipWrapper containerClassName="qes-text-input__tooltip-container" text={validationTooltipContent}><QesIcon iconName={QesIcons.InfoCircle} description="Info" width="16" /></TooltipWrapper> : ""}
          </span>
        ) : null}
      </label>
      <div className="qes-text-input__subcontainer">
        <div
          className={cx({
            "disabled": props.disabled,
            "error": hasBeenFocused && fieldState === FormFieldStates.Error,
            "focus": focused,
            "qes-text-input__group": true,
            [props.className]: !!props.className,
          })}
        >
          <input
            {...props}
            id={props.id || testId}
            type="datetime-local"
            className={cx({
              "error": hasBeenFocused && fieldState === FormFieldStates.Error,
              "qes-text-input__input": true,
            })}
            onFocus={() => setFocused(true)}
            ref={qesRef as RefObject<HTMLInputElement>}
            data-testid={testId}
            required={required}
          />
        </div>
        <div>
          {subText && <small className="qes-text-input__descriptive-text">{subText}</small>}
        </div>
      </div>
    </div>
  );
};
